<template>
  <ReferenceComponentParentList
    :searchList="result"
    :componentType="componentType"
    :selectedIndex="selectedIndex"
    :hideFooter="showTeams"
    :hideHeader="!currentTeam"
    :hideContent="showTeams"
    :isLast="isLastSprint"
    :showScreenOnly="showTeams"
    class="reference-list_md"
    @onClick="onSprintSelected"
    @onScroll="onScroll"
  >
    <template #header>
      <div
        v-if="currentTeam"
        class="reference-team reference-team_heading"
      >
        <div class="reference-team__left">
          <Avatar
            :src="currentTeam.picture?.pictureUrl || null"
            size="md"
            type="teamsGroup"
          />
        </div>
        <div class="reference-team__right">
          <BaseSubHeading
            weight="400"
            size="medium"
            color="monochrome-07"
          >
            {{ currentTeam.name }} ({{ currentTeam.size }})
          </BaseSubHeading>
        </div>
      </div>
      <div v-else>
        <BaseSubHeading
          weight="600"
          size="medium"
          color="monochrome-06"
        >
          Sprints
        </BaseSubHeading>
      </div>
    </template>

    <template #item="{ option }">
      <div class="reference-list__item-in reference-sprint">
        <SprintHeading
          :sprint="option"
          class="reference-sprint__title"
        />
      </div>
    </template>

    <template #footer>
      <button
        class="reference-list__link"
        @click.prevent="onSelectAnotherTeam"
      >
        Select Another Team
      </button>
    </template>

    <template
      v-if="showTeams"
      #screen
    >
      <Observer
        v-if="teams.length"
        :items="teams"
        :last="isLastTeam"
        :options="observerOptions"
        elementClass="reference-list__item"
        class="reference-list__list"
        @intersect="onScrollTeam"
      >
        <template #default="{ item }">
          <button
            class="reference-list__button"
            @click.prevent="onTeamSelect(item)"
          >
            <div class="reference-team">
              <div class="reference-team__left">
                <Avatar
                  :src="item.picture?.pictureUrl || null"
                  size="md"
                  type="teamsGroup"
                />
              </div>
              <div class="reference-team__right">
                <BaseSubHeading
                  weight="400"
                  size="medium"
                  color="monochrome-07"
                >
                  {{ item.name }} ({{ item.size }})
                </BaseSubHeading>
              </div>
            </div>
          </button>
        </template>
      </Observer>
      <div
        v-else-if="!$wait.is('referenceSprintSearch')"
        class="reference-loading"
      >
        No teams found.
      </div>
    </template>
  </ReferenceComponentParentList>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Action, Getter } from "s-vuex-class"

import Avatar from "@/components/Avatar/index.vue"
import Observer from "@/components/Observer/index.vue"
import ReferenceComponentListCommon from "@/components/Reference/helpers/ReferenceComponentListCommon"
import ReferenceComponentParentList from "@/components/Reference/helpers/ReferenceComponentParentList.vue"
import SprintHeading from "@/components/Sprint/SprintHeading.vue"

import { FilterTeam, Item, Project, SearchSprintResult, ServerResponse, Team } from "@/models"
import { FilterSprint } from "@/models/Filter/FilterSprint"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { Actions, Getters } from "@/store"
import { observerOptions, parsingServerResponseContent } from "@/utils/helpers"

const components = {
  ReferenceComponentParentList,
  Avatar,
  SprintHeading,
  Observer
}

@Options({
  name: "SprintList",
  components
})
export default class extends mixins(ReferenceComponentListCommon) {
  @Getter(Getters.GET_PROJECT) declare currentProject: Project
  @Getter(Getters.GET_ITEM) declare currentItem: Item
  @Getter(Getters.GET_SESSION_PROJECT_ID) readonly sessionProjectId: string

  @Action(Actions.SEARCH_TEAM) getTeams: (params: FilterTeam) => Promise<ServerResponse<Team>>
  @Action(Actions.SEARCH_SPRINT) searchSprint: ({
    filter,
    onlyGet
  }: {
    filter: FilterSprint
    onlyGet?: boolean
  }) => Promise<ServerResponse<SearchSprintResult>>

  waitTitle = "reference.search.sprint"
  componentType = ReferenceType.SPRINT
  showTeams = false
  currentTeam: Team | null = null
  teams: Team[] = []
  result: SearchSprintResult[] = []
  filterTeam = FilterTeam.createDefault()
  observerOptions = observerOptions
  isLastTeam = true
  isLastSprint = true

  get teamId() {
    return this.currentItem?.team?.id ?? this.currentTeam?.id ?? ""
  }

  async created() {
    this.$wait.start("referenceSprintSearch")
    this.filter = FilterSprint.createDefault()
    this.filter.addSort("sequenceNumber", false)
    this.filterTeam.demo = this.currentProject?.demo ?? false
    this.filterTeam.includeDemo = this.currentProject?.demo ?? false
    this.filterTeam.projectId = this.sessionProjectId
    await this.onGetTeams()
    this.currentTeam = this.teams.find(team => team.id === this.teamId) || null
    this.searchAction = this.onSearchSprint
    await this.onSearchSprint()
    this.$wait.end("referenceSprintSearch")
  }

  onScrollTeam() {
    this.filterTeam.page++
    this.onGetTeams()
  }

  async onGetTeams() {
    const serverResponse = await this.getTeams(this.filterTeam)
    this.isLastTeam = serverResponse.last
    this.teams = parsingServerResponseContent({
      originalContent: this.teams,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber
    })
  }

  onSelectAnotherTeam() {
    this.currentTeam = null
    this.onChangeTeam()
  }

  onChangeTeam() {
    this.showTeams = !this.showTeams
  }

  queryUpdated(q) {
    this.filter.q = q.substring(1)
    this.search()
  }

  onEnterHandler() {
    if (this.result) {
      this.onSprintSelected(this.result[this.selectedIndex])
    }
  }

  onSprintSelected(payload) {
    this.selectItem({
      item: Object.assign({}, payload, {
        team: this.currentTeam
      }),
      payload: {}
    })
  }

  onTeamSelect(team) {
    this.currentTeam = team
    this.resetSprint()
    this.onChangeTeam()
    this.search()
  }

  resetSprint() {
    this.result = []
    this.filter.page = 1
  }

  async onSearchSprint() {
    if (!this.currentTeam) {
      return
    }
    this.filter.teamIds = [this.currentTeam.id]
    this.filter.projectId = this.currentProject.id
    const serverResponse = await this.searchSprint({
      filter: this.filter,
      onlyGet: true
    })
    if (!serverResponse) return
    this.isLastSprint = serverResponse.last
    this.result = parsingServerResponseContent({
      originalContent: this.result,
      serverResponseContent: serverResponse.content,
      numberOfPage: serverResponse.pageable.pageNumber
    })
    this.selectedIndex = 0
  }
}
</script>
